import AiEntries from "../../../ai/AiEntries";
import useAiFeatures, { aiHref } from "../../../ai/useAiFeatures";
import React, { useMemo, useEffect, useRef } from "react";

import { Grid } from "@material-ui/core";

import IconLink from "./IconLink";
import MoreButton from "./MoreButton";
import ExpandedMenu from "./ExpandedMenu";

import HomeIcon from "../../../images/Icons/Home";
import CareerIcon from "../../../images/Icons/Career";
import CalendarIcon from "../../../images/Icons/Calendar";
import KnowledgeIcon from "../../../images/Icons/Knowledge";
import NotificationsIcon from "../../../images/Icons/Notifications";
import ParentIcon from "../../../images/Icons/Parent";

import { makeStyles } from "@material-ui/core/styles";
import setStyles from "../../../setStyles";
import { useReduxQuery } from "../../../hooks/useReduxQuery";
import { fetchNotifications } from "../../../api/notifications";
import Badge from "../../Badge";
import { useLocation } from "react-router-dom";
import { fetchUser } from "../../../api/user";
import { playerStatusTypes, userTypes } from "../../../utils/constants";

const useStyles = makeStyles((theme) => ({
  mainMenuContainer: {
    width: "437px",
    padding: "24px 0px",
    ...setStyles.setFlexRow,
    margin: "auto 0px auto auto",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      position: "static",
      margin: "auto",
      maxWidth: "500px",
    },
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      maxWidth: "300px",
      padding: "24px 0px",
    },
  },
  badgeContainer: {
    position: "relative",
  },
}));

const MainMenu = ({ openMenu, showMore, closeMenu, pathName }) => {
  const classes = useStyles();
  const aiFeatures = useAiFeatures();
  const { notifications } = useReduxQuery("Notifications", fetchNotifications);
  const { user } = useReduxQuery("User", fetchUser);

  const nbUnreadNotification = useMemo(
    () =>
      Object.values(notifications || []).filter((n) => !n.read)?.length || 0,
    [notifications]
  );

  const location = useLocation();
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    if (aiFeatures.status !== "ready" || aiFeatures.bot_mode !== "old_bot") return;
    if (scriptLoadedRef.current) {
      return;
    }

    if (location.pathname !== "/login") {
      if (!user?.userType) {
        return;
      }

      scriptLoadedRef.current = true;

      let url = "https://app.dante-ai.com/bubble-embed.js?"
      const defaultParams = {
        modeltype: "gpt-5",
        tabs: "false",
      }

      const userType = user?.userType
      const playerStatus = user?.status

      if (userType === userTypes.PLAYER) {
        if (playerStatus === playerStatusTypes.RETIRED) {
          // Retired player
          url += new URLSearchParams({
            ...defaultParams,
            kb_id: '20f324e0-1050-488d-a9a3-f596f635c91f',
            token: '64c0c7f4-cfa4-4e15-a251-5e6389c65d66'
          }).toString();
        } else {
          // Non-Retired player
          url += new URLSearchParams({
            ...defaultParams,
            kb_id: 'e5f0311b-ee71-4d01-adb4-7f284686acc2',
            token: 'c356ddd0-412f-41ed-b58c-99f6baf5a0c6'
          }).toString();
        }
      }
      if (userType === userTypes.COLLEGE) {
        url += new URLSearchParams({
          ...defaultParams,
          kb_id: '95a03610-6b2b-40e6-ac8d-1360c0580b93',
          token: 'c9166ccc-5efe-41ca-86ed-b9e79dcce08c'
        }).toString();
      } else if (userType === userTypes.COACH) {
        url += new URLSearchParams({
          ...defaultParams,
          kb_id: '2b4770fa-1540-4e05-bfdb-29544c0b421b',
          token: '382734de-b5cf-4cd1-a3dd-38d7eed281a5'
        }).toString();
      } else if (userType === userTypes.AGENT) {
        url += new URLSearchParams({
          ...defaultParams,
          kb_id: 'e8ee652c-27fd-4ffb-82ff-6b6772951b5d',
          token: '3583975b-8b3d-4550-8c1b-8a98769976c5'
        }).toString();
      } else if (userType === userTypes.EXECUTIVE) {
        url += new URLSearchParams({
          ...defaultParams,
          kb_id: '84a552eb-45c4-4b0f-add8-2693b15fecd5',
          token: 'db1ca5fc-8185-4421-924d-8bb0608b7e85'
        }).toString();
      } else if (userType === userTypes.PARENT || userType === userTypes.SPOUSE) {
        url += new URLSearchParams({
          ...defaultParams,
          kb_id: '040e377a-dafa-4758-ab34-d414e440bb1f',
          token: 'cb1b1eb4-4e71-47cd-808a-5523959ac7f5'
        }).toString();
      } else if (userType === userTypes.REPORTER) {
        url += new URLSearchParams({
          ...defaultParams,
          kb_id: 'b1ed776d-4e56-4ae2-a99c-8e65bcffb140',
          token: '2fef8ad8-9059-4184-8ef2-95863cc38939'
        }).toString();
      }

      const script = document.createElement("script");
      script.src = url;
      document.body.appendChild(script);
    }
  }, [location.pathname, user?.status, user?.userType, aiFeatures.status, aiFeatures.bot_mode]);

  return (
    <Grid item md={6} sm={12} className={classes.mainMenuContainer}>
      <IconLink text="Home" url="/" active={pathName === "/"}>
        <HomeIcon active={pathName === "/"} />
      </IconLink>
      {user?.userType !== userTypes.PARENT &&
        user?.userType !== userTypes.SPOUSE && (
          <IconLink
            text="Career"
            url="/contract"
            active={pathName === "/contract"}
          >
            <CareerIcon active={pathName === "/contract"} />
          </IconLink>
        )}
      {user?.userType === userTypes.PARENT && (
        <IconLink text="Parent" url="/parent" active={pathName === "/parent"}>
          <ParentIcon active={pathName === "/parent"} />
        </IconLink>
      )}
      {user?.userType === userTypes.SPOUSE && (
        <IconLink text="Spouse" url="/spouse" active={pathName === "/spouse"}>
          <ParentIcon active={pathName === "/spouse"} />
        </IconLink>
      )}
      <IconLink text="Events" url="/events" active={pathName === "/events"}>
        <CalendarIcon active={pathName === "/events"} />
      </IconLink>
      <IconLink
        text="Education"
        url="/playerEducation"
        active={pathName === "/playerEducation"}
      >
        <KnowledgeIcon active={pathName === "/playerEducation"} />
      </IconLink>
      <div className={classes.badgeContainer}>
        {nbUnreadNotification > 0 && <Badge nb={nbUnreadNotification} />}
        <IconLink text="Notifications" url="/notifications">
          <NotificationsIcon />
        </IconLink>
      </div>
      {aiFeatures.enabled && aiFeatures.bot_mode === "stratic" && aiHref("assistant") && <a href={aiHref("assistant")} aria-label="Open Stratic assistant" style={{position:"fixed",right:24,bottom:24,zIndex:900,padding:12,borderRadius:24,background:"#13264a",color:"white"}}>Ask Stratic</a>}
      <AiEntries variant="header" />
      <MoreButton moreHandler={openMenu} />
      <ExpandedMenu showMore={showMore} closeMenu={closeMenu} user={user} />
    </Grid>
  );
};

export default MainMenu;
